import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Divider, Modal } from "semantic-ui-react";
import * as _ from "lodash";
import { nameofFactory, hasWriteAccess, parseConfig } from "../../utils/utils";
import Permissions from "../../components/Permissions/Permissions";
import {
  getFaultPriorityOptionsByDomainId,
  getDomainOptionsByDomainId,
  getFaultCategoryOptions,
  getGroupNameByDomainId,
  getKceVersionOptions,
  getDataActOptions,
  getGrandNormOptions,
  getDuplicateEscalatorFaultBindings,
  getcommonEnumOptions,
  DropdownOption
} from "../../utils/pdApiUtils";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import CheckboxField from "../../components/Editing/CheckboxField";
import NumberField from "../../components/Editing/NumberField";
import DropdownField from "../../components/Editing/DropdownField";
import { EditActions, editActions } from "../../actions/editActions";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";
import EscalatorFaultBindings from "../../components/EscalatorFaultBindings/EscalatorFaultBindings";
import EscalatorFaultConfiguration from "../../components/EscalatorFaultConfiguration/EscalatorFaultConfiguration";

type StateProps = {
  editedFault: Api.AnyFault;
  storedFault: Api.AnyFault;
  pdApi: State.PdApi;
  activeUser?: Api.User;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

// For type checking
const faultFields = nameofFactory<Api.BaseFault>();
const driveFaultFields = nameofFactory<Api.DriveFault>();
const liftFaultFields = nameofFactory<Api.LiftFault>();
const groupFaultFields = nameofFactory<Api.GroupFault>();
const escalatorFaultFields = nameofFactory<Api.EscalatorFault>();
const doorFaultFields = nameofFactory<Api.DoorFault>();
const None = -1;

/**
 * EditFault props
 */
export type EditFaultProps = StateProps & DispatchProps & OwnProps;

export type EditFaultState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsFault[];
  locsEdited: Api.LocsFault[];
  userPermissions: boolean;
};

/**
 * EditFault component
 */
export class EditFault extends React.Component<EditFaultProps, EditFaultState> {
  private cachedEnumerations: Api.CommonEnumeration[] | null = null;
  private cachedEnumOptions: Record<string, DropdownOption[]> = {};

  constructor(props: EditFaultProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: [],
      userPermissions: hasWriteAccess(
        props.activeUser,
        getGroupNameByDomainId(props.editedFault.domain.id, props.pdApi)
      )
    };
  }
  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen, userPermissions } = this.state;
    const {
      editedFault,
      storedFault,
      activeUser,
      pdApi,
      toggleModal,
      columns,
      isShortenTextEnabled
    } = this.props;
    const groupName = getGroupNameByDomainId(editedFault.domain.id, pdApi);
    const isNew = !storedFault;
    const storeOption: Api.OptionsGrud =
      groupName === "NonKone"
        ? "nonKoneFaults"
        : groupName === "Escalator"
        ? "escalatorFaults"
        : groupName === "Door"
        ? "doorFaults"
        : groupName === "Drive"
        ? "driveFaults"
        : groupName === "Lift"
        ? "liftFaults"
        : groupName === "Group"
        ? "groupFaults"
        : "faults";

    if (storedFault) {
      storedFault.priority.url = pdApi.faultPriorities[storedFault.priority.id].url;
      if (storedFault.permissions.length > 0) {
        storedFault.permissions.forEach(permission => {
          if (permission.userGroup) {
            const index = _.findIndex(pdApi.userGroups, { id: permission.userGroup.id });
            permission.userGroup.url = pdApi.userGroups[index].url;
          }
        });
      }
    }
    return (
      <Editor
        itemName="fault"
        originalObject={storedFault}
        editedObject={editedFault}
        hasWritePermission={hasWriteAccess(activeUser, groupName)}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          this.props.editActions.setEdited({
            ...editedFault,
            locs: this.state.locsEdited
          });
          this.props.editActions.update(storeOption);
        }}
        create={() => {
          this.props.editActions.setEdited({
            ...editedFault,
            locs: this.state.locsEdited
          });
          this.props.editActions.add(storeOption);
        }}
        copy={() => {
          this.props.editActions.setEdited({
            ...editedFault,
            locs: this.state.locsEdited
          });
          this.props.editActions.startEditing(editedFault, true);
        }}
        remove={{ callback: toggleModal }}
        copyAccess={groupName === "Group" ? false : true}
      >
        {this.commonProperties(isNew, groupName)}
        <Divider horizontal={true}>{`${groupName} properties`}</Divider>
        {this.specificProperties(groupName)}
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"Fault"}
            pdApi={pdApi}
            editedItem={{ ...editedFault, locs: this.state.locsEdited }}
            getOption={
              groupName === "NonKone"
                ? "nonkonefaultLocalizations"
                : groupName === "Escalator"
                ? "escalatorfaults"
                : groupName === "Door"
                ? "doorfaults" // ! No Localizations endpoint for escalator/door faults
                : "faultLocalizations"
            }
            columns={columns["Fault Localization Status"]}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={userPermissions}
            locsOriginal={this.state.locsOriginal || []}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
          />
        </Modal>
      </Editor>
    );
  }

  private englishTranslationsChanged = (): boolean =>
    this.props.editedFault.englishName !== this.props.storedFault?.englishName ||
    this.props.editedFault.action !== this.props.storedFault?.action ||
    this.props.editedFault.reason !== this.props.storedFault?.reason;

  private handleEnglishTranslationsChange(item: Api.AnyFault) {
    const { storedFault, editedFault } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.englishName,
              reason: item.reason,
              action: item.action,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsFault
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.englishName,
                reason: item.reason,
                action: item.action
              } as Api.LocsFault;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedFault?.englishName,
              reason: storedFault?.reason,
              action: storedFault?.action,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsFault
          ]
        : locsOriginal;

    this.setState((prevState: EditFaultState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedFault,
      englishName: item.englishName,
      action: item.action,
      reason: item.reason
    });
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditFaultState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsFault[],
    oldLocalizations: Api.LocsFault[]
  ): void => {
    const { editedFault } = this.props;
    this.setState((prevState: EditFaultState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedFault,
      englishName:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
        editedFault?.englishName,
      reason:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.reason ||
        editedFault?.reason,
      action:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.action ||
        editedFault?.action,
      locs: newLocalizations
    });
  };

  private setLocalizationsNonEdited = (Localizations: Api.LocsFault[]): void => {
    this.setState((prevState: EditFaultState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };

  // Display common properties
  private commonProperties = (isNew: boolean, groupName: Api.GroupName): JSX.Element => {
    const { editedFault, pdApi } = this.props;
    const domainId = editedFault.domain.id;
    // Create options for priority dropdown
    const priorityOptions = getFaultPriorityOptionsByDomainId(domainId, pdApi);
    // Create options for priority dropdown
    let domainOptions = getDomainOptionsByDomainId(domainId, false, pdApi);

    // Hack: (currently) backend does not support faults for LIO, so it is now removed
    domainOptions = domainOptions.filter(domain => domain.text !== "KCELIO");

    return (
      <>
        {this.numberFieldAsString(
          "Code",
          faultFields("code"),
          !isNew,
          false,
          groupName === "Escalator"
            ? "The code must be unique in the domain."
            : "The code must be greater than zero and unique in the domain."
        )}
        {isNew ? (
          <DropdownField
            id="domainName"
            label="Domain name"
            value={editedFault.domain.id}
            options={domainOptions}
            search={true}
            onChange={id => {
              const domain = pdApi.domains[id];
              this.props.editActions.setEdited({ ...editedFault, domain });
            }}
          />
        ) : (
          <TextField
            id="domainNameText"
            label="Domain name"
            readonly={true}
            value={
              pdApi.domains[domainId].enumName + " [" + pdApi.domains[domainId].enumValue + "]"
            }
            isError={false}
            onChange={() => undefined}
          />
        )}
        <br />
        <DropdownField
          id="priority"
          label="Priority"
          value={editedFault.priority.id}
          options={priorityOptions}
          search={true}
          onChange={id => {
            const priority = {
              id: pdApi.faultPriorities[id].id,
              url: pdApi.faultPriorities[id].url
            };
            this.props.editActions.setEdited({ ...editedFault, priority });
          }}
          disabled={!this.state.userPermissions}
        />
        <br />
        {this.textField("Name", faultFields("name"), !this.state.userPermissions)}

        {groupName !== "NonKone" && (
          <>
            {this.textField("Comment", faultFields("comment"), !this.state.userPermissions)}
            {this.textField("Detection", faultFields("detection"), !this.state.userPermissions)}
            {this.textField("Recovery", faultFields("recovery"), !this.state.userPermissions)}
            {this.textField("Operation", faultFields("operation"), !this.state.userPermissions)}
            <Permissions
              permissions={editedFault.permissions}
              userGroups={pdApi.userGroups}
              onChanged={permissions => {
                this.handleEditedFaultChanged(faultFields("permissions"), permissions);
              }}
              disabled={!this.state.userPermissions}
            />
          </>
        )}

        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={this.englishTranslationsChanged()}
        />
        {this.textField("English name", faultFields("englishName"), !this.state.userPermissions)}
        {this.textField("Reason", faultFields("reason"), !this.state.userPermissions)}
        {this.textField("Action", faultFields("action"), !this.state.userPermissions)}
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={this.toggleEditLocalizationStatusModal}
        />
      </>
    );
  };

  // Select domain group specific property group
  private specificProperties = (groupName: Api.GroupName): JSX.Element => {
    if (groupName === "Drive") {
      return this.driveProperties();
    } else if (groupName === "Lift") {
      return this.liftProperties();
    } else if (groupName === "Escalator") {
      return this.escalatorProperties();
    } else if (groupName === "Door") {
      return this.doorProperties();
    } else if (groupName === "NonKone") {
      return this.nonKoneProperties();
    } else {
      return this.groupProperties();
    }
  };

  // Display lift properties
  private liftProperties = (): JSX.Element => {
    const { editedFault, pdApi } = this.props;
    const liftFault = editedFault as Api.LiftFault;
    const kceVersionOptions = getKceVersionOptions(pdApi.kceVersions);
    const valueFromVersion = liftFault.validFromVersion ? liftFault.validFromVersion.id : None;
    const valueUntilVersion = liftFault.validUntilVersion ? liftFault.validUntilVersion.id : None;
    
    const dataActOptions = getDataActOptions(pdApi.dataActCodes || []);
    const liftDomainGroupEnumValue = _.values(pdApi.domainGroups).find(
      domainGroup => domainGroup.enumName === "Lift"
    )?.enumValue;
    const grandNormOptions = getGrandNormOptions(
      (pdApi.grandNorms || []).filter(
        grandNorm => grandNorm.domainGroupId === liftDomainGroupEnumValue
      )
    );

    return (
      <>
        {this.textField(
          "Enumerations",
          liftFaultFields("enumName"),
          !this.state.userPermissions,
          false,
          false,
          !this.isEmptyString(liftFault.enumName)
            ? "The enumerations must be unique in the domain group."
            : ""
        )}
        <DropdownField
          id="dataAct"
          label="DataAct Category"
          value={liftFault.dataActId}
          options={dataActOptions}
          onChange={id => {
            const dataActId = id;
            this.props.editActions.setEdited({
              ...editedFault,
              dataActId
            });
          }}
          disabled={!this.state.userPermissions}
          search={true}
        />
        <DropdownField
          id="grandNorm"
          label="GrandNorm Category"
          value={liftFault.grandNormId}
          options={grandNormOptions}
          onChange={id => {
            const grandNormId = id;
            this.props.editActions.setEdited({
              ...editedFault,
              grandNormId
            });
          }}
          disabled={!this.state.userPermissions}
          search={true}
        />
        <DropdownField
          id="validFrom"
          label="Valid from version"
          value={valueFromVersion}
          options={kceVersionOptions}
          onChange={id => {
            const validFromVersion =
              id === None ? null : { id: pdApi.kceVersions[id].id, url: null };
            this.props.editActions.setEdited({
              ...editedFault,
              validFromVersion
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <DropdownField
          id="validUntil"
          label="Valid until version"
          value={valueUntilVersion}
          options={kceVersionOptions}
          onChange={id => {
            const validUntilVersion =
              id === None ? null : { id: pdApi.kceVersions[id].id, url: null };
            this.props.editActions.setEdited({
              ...editedFault,
              validUntilVersion
            });
          }}
          disabled={!this.state.userPermissions}
        />

        {this.textField(
          "Code variant",
          liftFaultFields("codeVariant"),
          !this.state.userPermissions,
          false,
          false,
          !this.isEmptyString(liftFault.codeVariant)
            ? "The Maximum number of characters must not exceed 10."
            : ""
        )}
        {this.textField(
          "Additional info def.",
          liftFaultFields("additionalInfoDef"),
          !this.state.userPermissions
        )}
        <div className="spaced-flex-row">
          {this.checkboxField(
            "Setup req.",
            liftFaultFields("setupReq"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Manual reset",
            liftFaultFields("manualReset"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Start prevented",
            liftFaultFields("startPrevented"),
            !this.state.userPermissions
          )}
          {this.checkboxField("SC Open", liftFaultFields("scOpen"), !this.state.userPermissions)}
          {this.checkboxField(
            "Change board",
            liftFaultFields("changeBoard"),
            !this.state.userPermissions
          )}
        </div>
      </>
    );
  };

  // Display drive properties
  private driveProperties = (): JSX.Element => {
    const { editedFault, pdApi } = this.props;
    const driveFault = editedFault as Api.DriveFault;
    const faultCategoryOptions = getFaultCategoryOptions(pdApi.faultCategories);
    return (
      <>
        {this.checkboxField(
          "A (Supervision can be adjusted with parameters)",
          "supervisionAdjustable",
          !this.state.userPermissions
        )}
        <DropdownField
          id="category"
          label="Category"
          value={driveFault.faultCategory.id}
          options={faultCategoryOptions}
          onChange={id => {
            const faultCategory = {
              id: pdApi.faultCategories[id].id,
              url: null
            };
            this.props.editActions.setEdited({ ...editedFault, faultCategory });
          }}
          disabled={!this.state.userPermissions}
        />
        {this.textField(
          "Drive additional data 1",
          driveFaultFields("data1Description"),
          !this.state.userPermissions
        )}
        {this.textField(
          "Drive additional data 2",
          driveFaultFields("data2Description"),
          !this.state.userPermissions
        )}
        {this.textField("Testing", driveFaultFields("testing"), !this.state.userPermissions)}
        {this.checkboxField(
          "Fault is localizable",
          driveFaultFields("localizable"),
          !this.state.userPermissions
        )}
      </>
    );
  };

  // Display group properties
  private groupProperties = (): JSX.Element => {
    const { editedFault } = this.props;
    const groupFault = editedFault as Api.GroupFault;
    return (
      <>
      {this.textField(
          "Enumerations",
          groupFaultFields("enumName"),
          !this.state.userPermissions,
          false,
          false,
          !this.isEmptyString(groupFault.enumName)
            ? "The enumerations must be unique in the domain group."
            : ""
        )}
      {this.textField(
        "Release status",
        groupFaultFields("releaseStatus"),
        !this.state.userPermissions
      )}
    </>
  );
};

  // Display escalator Properties
  private getEnumOptions = (enumName: string): DropdownOption[] => {
    const { enumerations } = this.props.pdApi;
    if (this.cachedEnumerations !== enumerations) {
      this.cachedEnumerations = enumerations;
      this.cachedEnumOptions = {};
    }
    if (!this.cachedEnumOptions[enumName]) {
      this.cachedEnumOptions[enumName] = getcommonEnumOptions(enumerations, enumName);
    }
    return this.cachedEnumOptions[enumName];
  };

  private escalatorProperties = (): JSX.Element => {
    const { editedFault, pdApi } = this.props;
    const escalatorFault = editedFault as Api.EscalatorFault;
    const escalatorDataActOptions = getDataActOptions(pdApi.escalatorDataActCodes || []);
    const escalatorFaultTypeOptions = this.getEnumOptions("E_faultType");
    const escalatorDisplayResetOptions = this.getEnumOptions("E_displayReset");
    const escalatorIndicatorOutputOptions = this.getEnumOptions("E_indicatorOutput");
    const escalatorStatisticsCounterOptions = this.getEnumOptions("E_statisticsCounter");
    const escalatorFaultLogOptions = this.getEnumOptions("E_faultLogToCustomer");
    const escalatorKrdOptions = this.getEnumOptions("E_krd");
    return (
      <>
        {this.textField(
          "Enumerations",
          escalatorFaultFields("enumName"),
          !this.state.userPermissions,
          false,
          false,
          !this.isEmptyString(escalatorFault.enumName)
            ? "The enumerations must be unique in the domain group."
            : ""
        )}
        <DropdownField
          id="dataAct"
          label="DataAct Category"
          value={escalatorFault.dataActId}
          options={escalatorDataActOptions}
          onChange={id => {
            const escalatorDataActId = id;
            this.props.editActions.setEdited({
              ...editedFault,
              dataActId: escalatorDataActId
            });
          }}
          disabled={!this.state.userPermissions}
          search={true}
        />
        {this.textField(
          "Additional data 1",
          escalatorFaultFields("data1Description"),
          !this.state.userPermissions
        )}
        {this.textField(
          "Additional data 2",
          escalatorFaultFields("data2Description"),
          !this.state.userPermissions
        )}
        {this.textField(
          "Additional data 3",
          escalatorFaultFields("data3Description"),
          !this.state.userPermissions
        )}
        {this.textField(
          "Additional data 4",
          escalatorFaultFields("data4Description"),
          !this.state.userPermissions
        )}
        {this.textField("Testing", escalatorFaultFields("testing"), !this.state.userPermissions)}
        <div className="spaced-flex-row">
          {this.checkboxField(
            "Setup req.",
            escalatorFaultFields("setupReq"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Manual reset",
            escalatorFaultFields("manualReset"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Start prevented",
            escalatorFaultFields("startPrevented"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "SC Open",
            escalatorFaultFields("scOpen"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Change board",
            escalatorFaultFields("changeBoard"),
            !this.state.userPermissions
          )}
        </div>
        <EscalatorFaultBindings
          domainId={escalatorFault.domain.id}
          faultId={escalatorFault.id ?? 0}
          bindings={escalatorFault.escalatorFaultBindings || []}
          escalatorVersions={pdApi.EscalatorVersions}
          domains={pdApi.domains}
          handleBindingsChanged={escalatorFaultBindings =>
            this.props.editActions.setEdited({
              ...escalatorFault,
              escalatorFaultBindings
            })
          }
          permissions={!this.state.userPermissions}
          storedbindings={this.props.storedFault as Api.EscalatorFault}
          editedbindings={this.props.editedFault as Api.EscalatorFault}
        />
        <EscalatorFaultConfiguration
          faultType={escalatorFault.faultType}
          displayReset={escalatorFault.displayReset}
          indicatorOutput={escalatorFault.indicatorOutput}
          statisticsCounter={escalatorFault.statisticsCounter}
          faultLog={escalatorFault.faultLog}
          krd={escalatorFault.krd}
          faultTypeOptions={escalatorFaultTypeOptions}
          displayResetOptions={escalatorDisplayResetOptions}
          indicatorOutputOptions={escalatorIndicatorOutputOptions}
          statisticsCounterOptions={escalatorStatisticsCounterOptions}
          faultLogOptions={escalatorFaultLogOptions}
          krdOptions={escalatorKrdOptions}
          handleFaultConfigChanged={this.handleFaultConfigChanged}
          permissions={!this.state.userPermissions}
        />
      </>
    );
  };

  // Display non-Kone Properties
  private nonKoneProperties = (): JSX.Element => <></>;
  // Display door Properties
  private doorProperties = (): JSX.Element => {
    const { editedFault } = this.props;
    const doorFault = editedFault as Api.DoorFault;
    return (
      <>
        {this.textField(
          "Enumerations",
          doorFaultFields("enumName"),
          !this.state.userPermissions,
          false,
          false,
          !this.isEmptyString(doorFault.enumName)
            ? "The enumerations must be unique in the domain group."
            : ""
        )}
      </>
    );
  };

  private handleFaultConfigChanged = (fieldName: string, value: string): void => {
    this.props.editActions.setEdited({
      ...this.props.editedFault,
      [fieldName]: value
    });
  };

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);
  private isFieldValid = (field: string): boolean => {
    const { editedFault } = this.props;
    switch (field) {
      case faultFields("code"):
        return this.isCodeValid();
      case faultFields("name"): {
        const { name } = editedFault;
        return !this.isEmptyString(name);
      }
      case faultFields("englishName"): {
        const { englishName } = editedFault;
        return !this.isEmptyString(englishName);
      }
      case faultFields("reason"): {
        const { reason } = editedFault;
        return !this.isEmptyString(reason);
      }
      case faultFields("action"): {
        const { action } = editedFault;
        return !this.isEmptyString(action);
      }
      case liftFaultFields("codeVariant"): {
        const { codeVariant } = editedFault as Api.LiftFault;
        return !this.isEmptyString(codeVariant) && codeVariant.length <= 10;
      }
      case liftFaultFields("enumName"):
        return this.isEnumNameValid();
      case escalatorFaultFields("enumName"):
        return this.isEnumNameValid();
      case doorFaultFields("enumName"):
        return this.isEnumNameValid();
      case groupFaultFields("enumName"):
        return this.isEnumNameValid();
      case groupFaultFields("releaseStatus"): {
        const { releaseStatus } = editedFault as Api.GroupFault;
        return !this.isEmptyString(releaseStatus);
      }
      case escalatorFaultFields("escalatorFaultBindings"): {
        const { escalatorFaultBindings, domain } = editedFault as Api.EscalatorFault;
        const escalatorVersions = this.props.pdApi.EscalatorVersions;
        return getDuplicateEscalatorFaultBindings(escalatorFaultBindings || [], escalatorVersions, domain.id).length === 0;
      }
      case escalatorFaultFields("faultType"):
      case escalatorFaultFields("displayReset"):
      case escalatorFaultFields("indicatorOutput"):
      case escalatorFaultFields("statisticsCounter"):
      case escalatorFaultFields("faultLog"):
      case escalatorFaultFields("krd"): {
        const fault = editedFault as Api.EscalatorFault;
        const configFields: Array<keyof Pick<Api.EscalatorFault, "faultType" | "displayReset" | "indicatorOutput" | "statisticsCounter" | "faultLog" | "krd">> =
          ["faultType", "displayReset", "indicatorOutput", "statisticsCounter", "faultLog", "krd"];
        if (configFields.includes(field as typeof configFields[number])) {
          const [, minVal, maxVal] = parseConfig(fault[field as typeof configFields[number]]);
          return minVal <= maxVal;
        }
        return true;
      }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedFault } = this.props;
    const temp = _.keys(editedFault);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };

  // Get status of edited code property. For targets other than Group and Escalators, Code must be unique in the domain and greater than zero.
  //For escalators and Group code must be unique in the domain and can be zero also.
  private isCodeValid = (): boolean => {
    const { pdApi, editedFault, storedFault } = this.props;
    const isNew = !storedFault;
    const groupName = getGroupNameByDomainId(editedFault.domain.id, pdApi);
    if (groupName === "Escalator") {
      return _.values(pdApi.escalatorFaults).find(
        (escalatorFault: Api.EscalatorFault) =>
          (isNew || escalatorFault.id !== editedFault.id) &&
          escalatorFault.domain.id === editedFault.domain.id &&
          escalatorFault.code === editedFault.code.toString()
      )
        ? false
        : true;
    } else if (groupName === "Door") {
      return parseInt(editedFault.code) <= 0 ||
        _.values(pdApi.doorFaults).find(
          (doorFault: Api.DoorFault) =>
            (isNew || doorFault.id !== editedFault.id) &&
            doorFault.domain.id === editedFault.domain.id &&
            doorFault.code === editedFault.code.toString()
        )
        ? false
        : true;
    } else if (groupName === "NonKone") {
      return parseInt(editedFault.code) <= 0 ||
        _.values(pdApi.nonKoneFaults).find(
          (nonKoneFault: Api.NonKoneFault) =>
            (isNew || nonKoneFault.id !== editedFault.id) &&
            nonKoneFault.domain.id === editedFault.domain.id &&
            nonKoneFault.code === editedFault.code.toString()
        )
        ? false
        : true;
    } else if (groupName === "Group") {
      return parseInt(editedFault.code) <= 0 || 
      _.values(pdApi.faults).find(
        (fault: Api.AnyFault) =>
          (isNew || fault.id !== editedFault.id) &&
          fault.domain.id === editedFault.domain.id &&
          fault.code === editedFault.code.toString()
      )
        ? false
        : true;
    } else
      return parseInt(editedFault.code) <= 0 ||
        _.values(pdApi.faults).find(
          (fault: Api.AnyFault) =>
            (isNew || fault.id !== editedFault.id) &&
            fault.domain.id === editedFault.domain.id &&
            fault.code === editedFault.code.toString()
        )
        ? false
        : true;
  };

  // Get status of edited enumName property. enumName must be unique in the domain and not empty.
  private isEnumNameValid = (): boolean => {
    const { pdApi, editedFault, storedFault } = this.props;
    const { domains, domainGroups } = pdApi;
    const isNew = !storedFault;
    const faultDomain = getGroupNameByDomainId(this.props.editedFault.domain.id, this.props.pdApi);
    const editedTargetFault = editedFault as Api.LiftFault | Api.EscalatorFault | Api.DoorFault |Api.GroupFault;
    const targetFaults =
      faultDomain === "Escalator"
        ? (pdApi.escalatorFaults as Api.EscalatorFault[])
        : faultDomain === "Door"
        ? (pdApi.doorFaults as Api.DoorFault[])
        : faultDomain === "Lift"
        ? pdApi.faults
            .filter(fault => {
              const groupId = domains[fault.domain.id]?.domainGroup.id;
              const groupName = domainGroups[groupId].enumName;
              if (groupName === "Lift") {
                return fault as Api.LiftFault;
              } else {
                return null;
              }
            })
            .filter(Boolean) // Filter out null values
        : faultDomain === "Group"
        ? pdApi.faults
            .filter(fault => {
              const groupId = domains[fault.domain.id]?.domainGroup.id;
              const groupName = domainGroups[groupId].enumName;
              if (groupName === "Group") {
                return fault as Api.GroupFault;
              } else {
                return null;
              }
            })
            .filter(Boolean) // Filter out null values
        : [];
    const isNotEmpty = !this.isEmptyString(editedTargetFault.enumName);
    const isUnique =
      _.values(targetFaults).find(targetfault => {
        if (
          (isNew || targetfault.id !== editedTargetFault.id) &&
          (targetfault as Api.LiftFault | Api.EscalatorFault | Api.DoorFault | Api.GroupFault).enumName
            ?.trim()
            .toLowerCase() === editedTargetFault.enumName?.trim().toLowerCase()
        ) {
          return targetfault;
        }
      }) === undefined;

    return isNotEmpty && isUnique;
  };

  // Shortcut for editing string property
  private textField = (
    label: string,
    field: string,
    readonly?: boolean,
    disabled?: boolean,
    isSingleLine?: boolean,
    errorMessage?: string
  ): JSX.Element => {
    const { editedFault, pdApi } = this.props;
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const value = editedFault[field];
    const groupName = getGroupNameByDomainId(editedFault.domain.id, pdApi);
    return (
      <TextField
        id={label.replace(/\s/g, "")}
        label={label}
        disabled={disabled}
        readonly={readonly}
        value={value}
        isError={!this.isFieldValid(field)}
        isSingleLine={isSingleLine}
        onChange={changedValue =>
          this.props.editActions.setEdited({
            ...editedFault,
            [field]:
              changedValue !== ""
                ? changedValue.trim()
                : changedValue === "" && groupName === "Escalator"
                ? null
                : changedValue.trim()
          })
        }
        errorMessage={errorMessage}
      />
    );
  };

  private checkboxField = (label: string, field: string, disabled?: boolean): JSX.Element => {
    const { editedFault } = this.props;
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const checked = editedFault[field];
    return (
      <CheckboxField
        id={label.replace(/\s/g, "")}
        label={label}
        checked={checked}
        onChange={value => this.props.editActions.setEdited({ ...editedFault, [field]: value })}
        disabled={disabled}
      />
    );
  };

  // Shortcut for editing number property
  private numberFieldAsString = (
    label: string,
    field: string,
    readOnly?: boolean,
    disabled?: boolean,
    errorMessage?: string
  ): JSX.Element => {
    const { editedFault } = this.props;
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    editedFault["code"] = editedFault["code"] === "" ? "0" : editedFault["code"];
    const numberValue: number = parseInt(editedFault["code"]);
    return (
      <NumberField
        id={label.replace(/\s/g, "")}
        label={label}
        disabled={disabled}
        readonly={readOnly}
        value={numberValue}
        isError={!this.isFieldValid(field)}
        onChange={value =>
          this.props.editActions.setEdited({ ...editedFault, [field]: value.toString() })
        }
        errorMessage={errorMessage}
      />
    );
  };

  /**
   * Handler of fault property changed
   */
  private handleEditedFaultChanged<T>(name: string, value: T) {
    let { editedFault } = this.props;
    editedFault = { ...editedFault, [name]: value };
    this.props.editActions.setEdited(editedFault);
  }
}

const mapStateToProps = (state: State.Root) => ({
  editedFault: state.editView.editedObject,
  storedFault: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditFault);
